// Schedule-a-meeting drawer (F-PLAT-7): a right-side slide-out with a title, a
// 3-line note, a calendar date picker, a 30-minute time list, and email invites.
// Admission (§5.4) stays the only gate; the time/note are metadata. Model A.

import { ChangeEvent, useCallback, useState } from 'react';

import { api, MeetingCreated } from '../api';
import { randomMeetingTitle } from '../lib/meetingTitles';
import { pushToast } from '../store/callStore';
import { Button } from '../ui/Button';
import { Drawer } from '../ui/Drawer';
import { Field, LabeledInput } from '../ui/Field';
import { IconCheck, IconDice, IconLock } from '../ui/icons';
import { DateField } from './DateField';
import { EmailChips } from './EmailChips';
import { TimeSelect } from './TimeSelect';

interface Props {
  onClose: () => void;
  /** Called after successful creation so the parent can refresh lists. */
  onCreated: (m: MeetingCreated) => void;
}

function defaultDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** The 30-minute slot after `hhmm`, clamped to the end of the day. */
function nextSlot(hhmm: string): string {
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  const total = Math.min(h * 60 + m + 30, 23 * 60 + 30);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

export function ScheduleDrawer({ onClose, onCreated }: Props) {
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(defaultDate);
  const [startTime, setStartTime] = useState('10:00');
  const [endTime, setEndTime] = useState('10:30');
  const [emails, setEmails] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<MeetingCreated | null>(null);
  const [copied, setCopied] = useState(false);
  const [invited, setInvited] = useState(0);

  const onTitle = useCallback((e: ChangeEvent<HTMLInputElement>) => setTitle(e.target.value), []);
  const onNote = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value), []);
  const rollTitle = useCallback(() => setTitle((t) => randomMeetingTitle(t)), []);
  // Keep end after start: bump it forward when a new start would overtake it.
  const onStart = useCallback((v: string) => {
    setStartTime(v);
    setEndTime((e) => (e <= v ? nextSlot(v) : e));
  }, []);

  const create = useCallback(() => {
    const startsAt = new Date(`${date}T${startTime}`);
    const endsAt = new Date(`${date}T${endTime}`);
    if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) {
      pushToast('Pick a valid date and time', 'warning');
      return;
    }
    if (endsAt <= startsAt) {
      pushToast('End time must be after the start time', 'warning');
      return;
    }
    setBusy(true);
    api
      .createMeeting({
        title: title.trim() || undefined,
        note: note.trim() || undefined,
        scheduledAt: startsAt.toISOString(),
        endsAt: endsAt.toISOString(),
      })
      .then((m) => {
        setCreated(m);
        onCreated(m);
        if (emails.length) {
          api
            .meetingInvite(m.slug, emails)
            .then((r) => setInvited(r.sent))
            .catch(() => undefined);
        }
      })
      .catch((e: unknown) => pushToast(e instanceof Error ? e.message : 'Failed', 'error'))
      .finally(() => setBusy(false));
  }, [title, note, date, startTime, endTime, emails, onCreated]);

  const copy = useCallback(() => {
    if (!created) return;
    void navigator.clipboard
      .writeText(`${window.location.origin}${created.joinPath}`)
      .then(() => setCopied(true));
  }, [created]);

  if (created) {
    return (
      <Drawer title="Meeting scheduled" onClose={onClose}>
        <div className="bz-schedule-done">
          <div className="bz-schedule-check">
            <IconCheck size={24} />
          </div>
          <p className="bz-auth-sub bz-schedule-done-sub">
            Share this link with your guests — they can join from any browser.
          </p>
          <div className="bz-created">
            <code className="bz-created-link">{`${window.location.origin}${created.joinPath}`}</code>
            <Button onClick={copy}>{copied ? 'Copied ✓' : 'Copy link'}</Button>
          </div>
          {invited > 0 && (
            <p className="bz-schedule-invited">
              Invited {invited} {invited === 1 ? 'person' : 'people'} by email.
            </p>
          )}
          <Button variant="primary" block onClick={onClose} className="bz-schedule-done-btn">
            Done
          </Button>
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer title="Schedule a meeting" onClose={onClose}>
      <LabeledInput
        label="Title"
        placeholder="Weekly product sync"
        value={title}
        onChange={onTitle}
        deep
        end={
          <button
            type="button"
            className="bz-input-dice"
            onClick={rollTitle}
            aria-label="Generate a random title"
            title="Surprise me"
          >
            <IconDice size={18} />
          </button>
        }
      />
      <Field label="Note (optional)">
        <textarea
          className="bz-textarea"
          rows={3}
          placeholder="Agenda, context, anything guests should know…"
          value={note}
          onChange={onNote}
        />
      </Field>
      <Field label="Date">
        <DateField value={date} onChange={setDate} />
      </Field>
      <div className="bz-timerange">
        <Field label="Starts">
          <TimeSelect value={startTime} onChange={onStart} />
        </Field>
        <Field label="Ends">
          <TimeSelect value={endTime} onChange={setEndTime} />
        </Field>
      </div>
      <Field label="Invite by email (optional)">
        <EmailChips emails={emails} onChange={setEmails} placeholder="name@example.com — Enter to add" />
      </Field>
      <div className="bz-option-row">
        <div>
          <div className="bz-option-title">Waiting room</div>
          <div className="bz-option-sub">
            Always on — guests wait until you admit them, and encryption keys are only released on
            admission.
          </div>
        </div>
        <span className="bz-option-lock">
          <IconLock size={15} />
        </span>
      </div>
      <div className="bz-modal-actions">
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={create} disabled={busy}>
          {busy ? 'Scheduling…' : 'Schedule meeting'}
        </Button>
      </div>
    </Drawer>
  );
}
